/**
 * 票链云 — 风险规则
 *
 * 本文件是风险判定的**唯一真相源**，前端展示与后端评级共用同一套规则，
 * 避免出现「前端显示高风险、后端放行」这类不一致。
 *
 * 规则依据：
 *   - 文档 5.5 校验报告与风险标记（风险等级 高/中/低/无，风险项清单）
 *   - 文档 4.1 企查查「不做强制拦截，仅输出风险提示」（超范围 = 中风险，不阻断）
 *   - 文档 4.3 红冲/作废需专项审批放行（= 高风险，默认阻断流转 T+）
 *   - 文档七 接口不可用 → 标记「外部校验暂不可用」并允许人工接管
 */
import { RiskItemCode, RiskLevel, RISK_LEVEL_WEIGHT, } from './enums.js';
/**
 * 风险项 → 等级的固定映射。
 *
 * 集中在此处而非分散在调用点，是为了让「哪个风险算几级」有唯一出处，
 * 调整风控力度时只改这一张表。
 */
export const RISK_ITEM_LEVEL = {
    // —— 高风险：票据本身失效或供应商主体异常，默认禁止流转 T+ ——
    [RiskItemCode.RED_FLUSHED]: RiskLevel.HIGH,
    [RiskItemCode.VOIDED]: RiskLevel.HIGH,
    [RiskItemCode.SUPPLIER_ABNORMAL]: RiskLevel.HIGH,
    // —— 中风险：需要人工判断但不阻断（文档 4.1 明确不做强制拦截）——
    [RiskItemCode.PARTIAL_RED]: RiskLevel.MEDIUM,
    [RiskItemCode.SCOPE_MISMATCH]: RiskLevel.MEDIUM,
    [RiskItemCode.CONTRACT_MISMATCH]: RiskLevel.MEDIUM,
    [RiskItemCode.TAX_MISMATCH]: RiskLevel.MEDIUM,
    [RiskItemCode.TAX_NOT_FOUND]: RiskLevel.MEDIUM,
    // —— 低风险：数据质量问题，修正即可 ——
    [RiskItemCode.OCR_LOW_CONFIDENCE]: RiskLevel.LOW,
    [RiskItemCode.FIELD_MISSING]: RiskLevel.LOW,
    [RiskItemCode.CONTRACT_MISSING]: RiskLevel.LOW,
    // 票面被人工改过而校验没重跑——结论可能对不上票，需要在初审前暴露出来
    [RiskItemCode.CHECK_STALE]: RiskLevel.MEDIUM,
    [RiskItemCode.TAX_QUERY_LIMIT]: RiskLevel.LOW,
    // —— 外部校验不可用：本身不是发票的风险，而是能力缺口，
    //    按低风险记录并强制人工接管（文档七）——
    [RiskItemCode.CHECK_UNAVAILABLE]: RiskLevel.LOW,
    /* —— 费用报销（延伸需求 3.3）——
     *
     * 【为什么重复报销是高风险】
     * 与红冲、作废同档：一票多报是主观套现意图，不是录入失误。
     * 高风险默认阻断**自动**流转 T+，人工仍可走特例放行——
     * 系统不剥夺人的判断，只是不替人放行。
     * 其余三项是判断问题（抬头、金额、品类），与既有的"超经营范围"同档：中风险、不阻断。
     */
    [RiskItemCode.EXPENSE_DUPLICATE]: RiskLevel.HIGH,
    [RiskItemCode.EXPENSE_BUYER_MISMATCH]: RiskLevel.MEDIUM,
    [RiskItemCode.EXPENSE_AMOUNT_MISMATCH]: RiskLevel.MEDIUM,
    [RiskItemCode.EXPENSE_CATEGORY_MISMATCH]: RiskLevel.MEDIUM,
    [RiskItemCode.EXPENSE_BUDGET_EXCEEDED]: RiskLevel.MEDIUM,
};
/**
 * 默认阻断流转 T+ 的风险等级。
 *
 * 文档 4.3 / 5.3：红冲、作废默认禁止流转到 T+，必须人工专项审批才允许特例放行。
 * 注意——这里阻断的是「自动流转」，人工仍可走特例放行通道，
 * 系统不做不可绕过的硬拦截（文档 4.1 反复强调不做强制拦截）。
 */
export const BLOCKING_RISK_LEVEL = RiskLevel.HIGH;
/** 根据风险项清单汇总出整单风险等级（取最高） */
export function summarizeRiskLevel(items) {
    let max = RiskLevel.NONE;
    for (const item of items) {
        if (RISK_LEVEL_WEIGHT[item.level] > RISK_LEVEL_WEIGHT[max]) {
            max = item.level;
        }
    }
    return max;
}
/**
 * 判断是否阻断自动流转 T+。
 * 返回原因列表，便于在 API 响应里直接告诉前端「为什么不给推」。
 */
export function shouldBlockTplusPush(items) {
    const reasons = items.filter((i) => RISK_LEVEL_WEIGHT[i.level] >= RISK_LEVEL_WEIGHT[BLOCKING_RISK_LEVEL]);
    return { blocked: reasons.length > 0, reasons };
}
/**
 * 是否需要人工专项审批放行。
 * 高风险一律需要，与阻断判定同源，但语义上区分开：
 * 「阻断」是默认行为，「专项审批」是放行路径。
 */
export function requiresSpecialRelease(items) {
    return shouldBlockTplusPush(items).blocked;
}
/** 生成风险项 —— 统一文案，避免各处手写导致措辞不一 */
export function makeRiskItem(code, message, evidence) {
    const item = {
        code,
        level: RISK_ITEM_LEVEL[code],
        message,
    };
    if (evidence !== undefined)
        item.evidence = evidence;
    return item;
}
/** 常用风险项构造器 —— 文案与文档措辞对齐 */
export const RiskItems = {
    redFlushed: (blueInvoiceNo) => makeRiskItem(RiskItemCode.RED_FLUSHED, '税务系统显示该发票已冲红，默认禁止流转 T+，如需入账请走特例放行并填写专项审批意见。', blueInvoiceNo ? { blueInvoiceNo } : undefined),
    voided: () => makeRiskItem(RiskItemCode.VOIDED, '税务系统显示该发票已作废，默认禁止流转 T+，请退回经办人要求供应商重新开具。'),
    partialRed: (blueInvoiceNo) => makeRiskItem(RiskItemCode.PARTIAL_RED, '税务系统显示该发票部分冲红，请核对冲红金额后确认剩余金额可入账。', blueInvoiceNo ? { blueInvoiceNo } : undefined),
    supplierAbnormal: (status) => makeRiskItem(RiskItemCode.SUPPLIER_ABNORMAL, `供应商经营状态为「${status}」，存在主体风险，请核实后再决定是否入账。`, { status }),
    scopeMismatch: (confidence, itemNames) => makeRiskItem(RiskItemCode.SCOPE_MISMATCH, `开票项目「${itemNames.join('、')}」可能超出供应商经营范围（匹配置信度 ${confidence}%）。系统不做强制拦截，请财务初审人工判断：确属临时性业务的可放行并留痕。`, { confidence, itemNames }),
    contractMismatch: (detail) => makeRiskItem(RiskItemCode.CONTRACT_MISMATCH, `发票与关联合同不一致：${detail}`),
    taxMismatch: (detail) => makeRiskItem(RiskItemCode.TAX_MISMATCH, `票面信息与税务系统记录不一致：${detail}`),
    taxNotFound: () => makeRiskItem(RiskItemCode.TAX_NOT_FOUND, '税务系统查无此票，可能是当日开具（发票数据 T+1 同步）或票面要素录入有误，请核对后重试。'),
    ocrLowConfidence: (fields, confidence) => makeRiskItem(RiskItemCode.OCR_LOW_CONFIDENCE, `OCR 识别置信度偏低（${confidence}%），字段「${fields.join('、')}」可能不准确，请人工核对票面原件后修正。`, { fields, confidence }),
    fieldMissing: (fields) => makeRiskItem(RiskItemCode.FIELD_MISSING, `缺少关键字段：${fields.join('、')}，请人工补录后再提交校验。`, { fields }),
    taxQueryLimit: () => makeRiskItem(RiskItemCode.TAX_QUERY_LIMIT, '该张发票当日查验次数已超限（税务查验平台限制每日每票 5 次），请次日重试。'),
    checkUnavailable: (provider, reason) => makeRiskItem(RiskItemCode.CHECK_UNAVAILABLE, `${provider}外部校验暂不可用（${reason}），已跳过该项自动校验。请人工核验后勾选确认并填写核验说明，业务不因此卡死。`, { provider, reason }),
    checkStale: (fields) => makeRiskItem(RiskItemCode.CHECK_STALE, `识别结果已被人工修正（涉及：${fields.join('、')}），但当前校验结果仍基于修正前的票面。请重新执行校验后再审批，否则校验结论与票面可能不符。`, { fields }),
    contractMissing: () => makeRiskItem(RiskItemCode.CONTRACT_MISSING, '该发票未关联合同编号，建议补充关联以便后续审计追溯。'),
    /* —— 费用报销专属 —— */
    expenseDuplicate: (otherBizNo, amountCents) => makeRiskItem(RiskItemCode.EXPENSE_DUPLICATE, `该发票已用于报销单「${otherBizNo}」，不能重复报销。若上一张报销单已退回或撤销，` +
        '请先在那边释放发票再重新提交。默认禁止流转 T+，确需放行请走特例放行并填写依据。', amountCents === undefined ? { otherBizNo } : { otherBizNo, amountCents }),
    expenseBuyerMismatch: (expected, actual) => makeRiskItem(RiskItemCode.EXPENSE_BUYER_MISMATCH, `发票抬头为「${actual || '（票面未识别到）'}」，与本企业「${expected}」不一致。` +
        '抬头不是本企业的发票不能作为本公司费用凭证，请退回经办人换票。', { expected, actual }),
    expenseAmountMismatch: (reimburseCents, invoiceCents) => makeRiskItem(RiskItemCode.EXPENSE_AMOUNT_MISMATCH, `报销金额与发票价税合计不一致（报销 ${(reimburseCents / 100).toFixed(2)} 元，` +
        `发票 ${(invoiceCents / 100).toFixed(2)} 元）。差额需说明原因，或按实际金额更正明细。`, { reimburseCents, invoiceCents }),
    expenseCategoryMismatch: (expenseType, categories) => makeRiskItem(RiskItemCode.EXPENSE_CATEGORY_MISMATCH, `开票项目「${categories.join('、')}」与所选费用类型「${expenseType}」看起来不符。` +
        '品类词表不可能穷尽，如属正常情况可放行并留痕。', { expenseType, categories }),
    expenseBudgetExceeded: (budgetName, budgetCents, usedCents) => makeRiskItem(RiskItemCode.EXPENSE_BUDGET_EXCEEDED, `「${budgetName}」预算已超支（预算 ${(budgetCents / 100).toFixed(2)} 元，` +
        `累计 ${(usedCents / 100).toFixed(2)} 元）。是否放行由审批人决定，系统不强制拦截。`, { budgetName, budgetCents, usedCents }),
};
/** 用于前端排序：风险项按等级从高到低 */
export function sortRiskItems(items) {
    return [...items].sort((a, b) => RISK_LEVEL_WEIGHT[b.level] - RISK_LEVEL_WEIGHT[a.level]);
}
//# sourceMappingURL=riskRules.js.map